import { AddOutlined, CheckCircle, SearchOutlined } from '@mui/icons-material'
import { Alert, Box, CircularProgress, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import EllipsisTypography from '@/components/dataDisplay/EllipsisTypography'
import { useSearchResourcesQuery } from '@/features/prequalification/localControl/hooks/useSearchResourcesQuery'
import type { ResourceSetMember } from '@/features/prequalification/localControl/types'
import { settleInSx } from '@/features/prequalification/localControl/utils/motion'

const SEARCH_DEBOUNCE_MS = 300

type AddResourcesPanelProps = {
  /** Resource ids already in the basket, so the panel can reflect membership. */
  selectedIds: Set<string>
  /** Toggle a single resource's membership. */
  onToggle: (resource: ResourceSetMember) => void
  /** Hide the panel's own heading when the surrounding container already titles it (e.g. a dialog). */
  hideTitle?: boolean
}

type SearchProps = Pick<AddResourcesPanelProps, 'selectedIds' | 'onToggle'>

function SearchResultsList({
  results,
  selectedIds,
  onToggle,
}: Readonly<{ results: ResourceSetMember[] } & SearchProps>) {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        maxHeight: 360,
        overflowY: 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      {results.map((resource, index) => {
        const added = selectedIds.has(resource.resourceId)
        // State-aware accessible name so a screen reader announces both the resource
        // and the action; `aria-pressed` carries the in-basket state for AT.
        const toggleLabel = added
          ? t('prequalifications.resource_set.add_resources.remove_resource', { name: resource.name })
          : t('prequalifications.resource_set.add_resources.add_resource', { name: resource.name })
        return (
          <Stack
            key={resource.resourceId}
            aria-label={toggleLabel}
            aria-pressed={added}
            data-testid="search-result-row"
            role="button"
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              minHeight: 44,
              cursor: 'pointer',
              borderTop: index === 0 ? 'none' : '1px solid',
              borderColor: 'divider',
              transition: 'background-color 120ms ease',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
            tabIndex={0}
            onClick={() => onToggle({ resourceId: resource.resourceId, name: resource.name })}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onToggle({ resourceId: resource.resourceId, name: resource.name })
              }
            }}
          >
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <EllipsisTypography tooltipTitle={resource.name} variant="body2">
                {resource.name}
              </EllipsisTypography>
              <Typography color="text.secondary" variant="caption">
                {resource.resourceId}
              </Typography>
            </Stack>
            {added ? (
              <CheckCircle color="success" fontSize="small" sx={settleInSx} />
            ) : (
              <AddOutlined color="action" fontSize="small" />
            )}
          </Stack>
        )
      })}
    </Box>
  )
}

function SearchMode({ selectedIds, onToggle }: Readonly<SearchProps>) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  const { resources, isFetching, isError } = useSearchResourcesQuery(debouncedQuery)
  const hasQuery = debouncedQuery.trim().length > 0
  const results = resources ?? []

  function renderBody() {
    if (!hasQuery) {
      return (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }} variant="body2">
          {t('prequalifications.resource_set.add_resources.search_hint')}
        </Typography>
      )
    }
    if (isFetching) {
      return (
        <Stack sx={{ alignItems: 'center', py: 4 }}>
          <CircularProgress aria-label={t('common.loading')} size={24} />
        </Stack>
      )
    }
    // A failed search must read as a failure, not as "no resources match" — that
    // ambiguity hides genuine lookup errors (e.g. a pasted id that 500s).
    if (isError) {
      return (
        <Alert severity="error" sx={{ mt: 1 }}>
          {t('prequalifications.resource_set.add_resources.search_error')}
        </Alert>
      )
    }
    if (results.length === 0) {
      return (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }} variant="body2">
          {t('prequalifications.resource_set.add_resources.no_results')}
        </Typography>
      )
    }
    return <SearchResultsList results={results} selectedIds={selectedIds} onToggle={onToggle} />
  }

  return (
    <Stack sx={{ gap: 2, flex: 1, minHeight: 0 }}>
      <TextField
        fullWidth
        placeholder={t('prequalifications.resource_set.add_resources.search_placeholder')}
        slotProps={{
          htmlInput: { 'aria-label': t('prequalifications.resource_set.add_resources.search_label') },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {/* Announce loading / error / empty transitions to assistive tech. */}
      <Box aria-live="polite" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {renderBody()}
      </Box>
    </Stack>
  )
}

/**
 * The builder's right pane: search for resources and toggle them into the basket.
 */
function AddResourcesPanel({ selectedIds, onToggle, hideTitle = false }: Readonly<AddResourcesPanelProps>) {
  const { t } = useTranslation()

  return (
    <Stack sx={{ gap: 2, height: '100%' }}>
      {!hideTitle && <Typography variant="h6">{t('prequalifications.resource_set.add_resources.title')}</Typography>}

      <Alert severity="info" variant="outlined">
        {t('prequalifications.resource_set.add_resources.local_control_disclaimer')}
      </Alert>

      <SearchMode selectedIds={selectedIds} onToggle={onToggle} />
    </Stack>
  )
}

export default AddResourcesPanel
